// What the road is doing, on the console that sent the driver.
//
// This console used to be blind by construction, and said so on the
// board: *"what you sent, not what the road is doing"*. That was true of
// the code and never true of the protocol. A run publishes to whoever
// holds its link, and the dispatcher holds every one of them — it minted
// them. Following its own jobs needs no server, no account, and no new
// capability: it is the customer's 78 bytes, read by the machine that
// issued them.
//
// Two things follow from that, and they are the reason this file is
// small:
//
// - **The console is one more subscriber.** It reads the same records,
//   through the same validation pipeline, and derives the same claims as
//   the page a customer opens. There is no privileged channel, so there
//   is no second reading of the truth to keep honest.
// - **It knows the plan.** A customer's link carries no plan (§20.8), so
//   their page can only say "not before". The dispatcher has the stops
//   in front of them, so passing the plan in yields a per-stop arrival
//   and a real progress count — the one respect in which this view is
//   allowed to be richer than the customer's.

import { useEffect, useRef, useState } from "react";
import type { FollowHandle, FollowSnapshot, RunPlan, WayfindSession } from "@wayfind/core";
import type { StoredJob, StoredStop } from "./state";

/** A job worth following, reduced to what the follow needs. */
export interface WatchTarget {
  id: string;
  /** The customer's link, as this console minted it. */
  trackUrl: string;
  stops: StoredStop[];
  /** Absolute expiry, unix seconds. A dead capability opens nothing. */
  notAfter: number;
}

/**
 * The capability out of a link this console minted.
 *
 * The engine does its own fragment parsing and would base64-decode
 * whatever it is handed, so a URL with a recipient card on it — which is
 * what goes out by SMS — has to be split first. `trackUrl` on a job is
 * the bare capability, but the split is cheap and this must not be the
 * place that breaks when somebody passes the customer's copy instead.
 */
export function capabilityOf(trackUrl: string): string {
  const hash = trackUrl.lastIndexOf("#");
  const fragment = (hash >= 0 ? trackUrl.slice(hash + 1) : trackUrl).trim();
  // `~` separates the capability from the recipient's own stop (§20.2).
  const [capability = ""] = fragment.split("~");
  return capability;
}

/** The plan as the engine wants it, from the stops this console holds. */
function planOf(stops: StoredStop[]): RunPlan | null {
  if (!stops.length) return null;
  return {
    stops: stops.map((stop, index) => ({
      index: index + 1,
      lat: stop.lat,
      lon: stop.lon,
      label: stop.label
    }))
  };
}

/** Whether a job is worth holding a subscription open for. */
export function watchable(job: StoredJob, nowSeconds: number): boolean {
  if (!job.trackUrl) return false;
  // An expired capability cannot be opened at all: the tag is derived
  // from key material the link no longer authorises.
  if (job.notAfter > 0 && job.notAfter <= nowSeconds) return false;
  return true;
}

/**
 * Follows every job handed in, and returns the newest snapshot for each.
 *
 * Keyed by job id rather than by link, because two jobs can legitimately
 * carry the same capability — a route's days all publish under the term's
 * link (§21.11), which is the whole point of it.
 *
 * Subscriptions are opened once per job and closed when the job leaves
 * the list or the session goes away. Nothing here polls: the engine
 * pushes, and a run that has gone quiet says so through its own staleness
 * rather than through this file inventing a timer.
 */
export interface JobWatch {
  /** The newest snapshot per job id. */
  snapshots: Map<string, FollowSnapshot>;
  /**
   * §20.7 — how to fetch a proof photo for one job, or null when this
   * console holds no follow for it. Deliberately not on the snapshot:
   * a snapshot is a value that gets copied into React state, and a
   * live handle is not.
   */
  proofOf(jobId: string): FollowHandle["proof"] | null;
}

export function useJobWatch(
  session: WayfindSession | null,
  targets: WatchTarget[]
): JobWatch {
  const [snapshots, setSnapshots] = useState<Map<string, FollowSnapshot>>(new Map());
  // Handles outlive renders and must never be recreated by one: opening a
  // second follow for a job already followed would double every
  // subscription on the mesh and leak the first.
  const handles = useRef(new Map<string, FollowHandle | "opening">()).current;

  // The identity of the set being watched, so a re-render with the same
  // jobs does not tear down live subscriptions.
  const key = targets.map(target => target.id).sort().join("|");

  useEffect(() => {
    if (!session) return;
    let live = true;

    for (const target of targets) {
      if (handles.has(target.id)) continue;
      handles.set(target.id, "opening");
      void (async () => {
        try {
          const handle = await session.follow(capabilityOf(target.trackUrl), {
            plan: planOf(target.stops)
          });
          if (!live) { handle.stop(); handles.delete(target.id); return; }
          handles.set(target.id, handle);
          setSnapshots(current => new Map(current).set(target.id, handle.store.get()));
          handle.store.subscribe(value => {
            setSnapshots(current => new Map(current).set(target.id, value));
          });
        } catch {
          // A link that will not open is not a broken console. The board
          // shows what was sealed and says nothing was heard, which is
          // exactly what a run that has not started looks like.
          handles.delete(target.id);
        }
      })();
    }

    // Anything no longer on the list stops costing a subscription.
    const wanted = new Set(targets.map(target => target.id));
    for (const [id, handle] of [...handles]) {
      if (wanted.has(id)) continue;
      if (handle !== "opening") handle.stop();
      handles.delete(id);
      setSnapshots(current => {
        if (!current.has(id)) return current;
        const next = new Map(current);
        next.delete(id);
        return next;
      });
    }

    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, key]);

  // The session going away takes every follow with it.
  useEffect(() => () => {
    for (const [, handle] of handles) if (handle !== "opening") handle.stop();
    handles.clear();
  }, [handles]);

  return {
    snapshots,
    proofOf(jobId) {
      const handle = handles.get(jobId);
      return handle && handle !== "opening" ? handle.proof : null;
    }
  };
}
